import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard,
  Mail,
  ArrowRight,
  ShieldCheck,
  Check,
  Clock,
  Sparkles,
  Building2,
  LogOut,
  KeyRound,
  PlayCircle,
} from "lucide-react";
import SEO from "../components/SEO";
import { useProAuth } from "../components/ProAuthContext";
import VerifyEmailBanner from "../components/VerifyEmailBanner";
import ImTopbarButton from "../im/ImTopbarButton";
import { API } from "../apiBase";

/**
 * MssanteBienvenue — Post-login / post-signup welcome dashboard.
 * --------------------------------------------------------------
 * Lets the freshly authenticated pro user pick between:
 *   1) Launching the platform configurator (/studio)
 *   2) Creating a MSSanté mailbox (/mssante/creer-boite)
 *
 * Also shows a 4-row status recap (Account, MSSanté, Studio project, PSC).
 * Statuses are read live from the user profile + /pro/mailboxes + /pro/studio/projects.
 */
export default function MssanteBienvenue() {
  const { user, logout, refresh } = useProAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isFreshSignup = params.get("welcome") === "1";

  const [mailboxes, setMailboxes] = useState([]);
  const [studioProjects, setStudioProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [mbRes, spRes] = await Promise.all([
          axios.get(`${API}/pro/mailboxes`).catch(() => ({ data: [] })),
          axios.get(`${API}/pro/studio/projects`).catch(() => ({ data: [] })),
        ]);
        if (alive) {
          setMailboxes(Array.isArray(mbRes.data) ? mbRes.data : []);
          setStudioProjects(Array.isArray(spRes.data) ? spRes.data : []);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/mssante", { replace: true });
  };

  // --- Dynamic status computation ------------------------------------------
  const accountActive = !!(user && user.id);
  const emailVerified = !!(user && user.email_verified !== false);
  const hasMailbox = mailboxes.length > 0;
  const hasStudioProject = studioProjects.length > 0;
  // Surface the most recently updated draft (status="draft") so the user can
  // pick the simulation right where they left it.
  const pendingDraft = studioProjects.find((p) => p.status === "draft") || null;

  const statuses = [
    {
      key: "account",
      icon: ShieldCheck,
      label: "Compte",
      sublabel: user?.email || "",
      ...(accountActive && emailVerified
        ? { tone: "ok", text: "Actif" }
        : accountActive
        ? { tone: "warn", text: "Email à vérifier" }
        : { tone: "muted", text: "Inactif" }),
    },
    {
      key: "mssante",
      icon: Mail,
      label: "Messagerie MSSanté",
      sublabel: hasMailbox
        ? `${mailboxes.length} boîte(s) commandée(s)`
        : "Aucune boîte créée",
      ...(hasMailbox
        ? { tone: "ok", text: "Configurée" }
        : { tone: "muted", text: "Non configurée" }),
    },
    {
      key: "studio",
      icon: LayoutDashboard,
      label: "Projet plateforme",
      sublabel: hasStudioProject
        ? `${studioProjects.length} simulation(s)`
        : "Aucune simulation lancée",
      ...(hasStudioProject
        ? { tone: "ok", text: "En cours" }
        : { tone: "muted", text: "Non démarré" }),
    },
    {
      key: "psc",
      icon: KeyRound,
      label: "Pro Santé Connect",
      sublabel: "Authentification e-CPS / OAuth2",
      tone: "soon",
      text: "Bientôt disponible",
    },
  ];

  return (
    <div className="bg-[var(--mc-navy-950)] min-h-screen" data-testid="page-mssante-bienvenue">
      <SEO title="Bienvenue · MetaConnect" canonical="/mssante/bienvenue" noIndex />

      {/* HEADER */}
      <section className="hex-grid pt-32 pb-10">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[var(--mc-mint)] mb-2">
                <Sparkles size={13} />
                {isFreshSignup ? "Espace activé" : "Espace sécurisé"}
              </div>
              <h1 className="font-display text-[#0F2B5B] text-4xl sm:text-5xl font-semibold leading-[1.08]">
                Bienvenue sur <span className="text-[var(--mc-mint)]">MetaConnect</span>
                {user?.first_name && (
                  <span className="text-[#0F2B5B]">, {user.first_name}</span>
                )}
              </h1>
              <p className="mt-3 text-[15px] text-[var(--mc-muted)] max-w-xl leading-relaxed">
                Que souhaitez-vous faire en premier&nbsp;?
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <ImTopbarButton />
              <Link
                to="/mssante/espace"
                data-testid="bienvenue-go-espace"
                className="btn-ghost px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5"
              >
                <Building2 size={13} /> Mon espace pro
              </Link>
              <button
                onClick={handleLogout}
                data-testid="bienvenue-logout"
                className="btn-ghost px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5"
              >
                <LogOut size={13} /> Se déconnecter
              </button>
            </div>
          </div>

          {isFreshSignup && (
            <div
              className="mt-6 rounded-xl border border-[rgba(110,231,197,0.3)] bg-[rgba(110,231,197,0.06)] p-5 flex items-start gap-3"
              data-testid="bienvenue-fresh-signup-banner"
            >
              <div className="grid place-items-center h-9 w-9 rounded-full bg-[rgba(110,231,197,0.18)] text-[var(--mc-mint)] shrink-0">
                <Check size={18} />
              </div>
              <div>
                <div className="text-[#0F2B5B] font-medium text-[14px]">
                  Votre espace sécurisé est créé
                </div>
                <p className="text-[12.5px] text-[var(--mc-muted)] mt-0.5">
                  Un email de bienvenue a été envoyé à {user?.email}. Choisissez ci-dessous votre prochaine étape.
                </p>
              </div>
            </div>
          )}

          {user && user.email_verified === false && (
            <div className="mt-6">
              <VerifyEmailBanner email={user.email} onVerified={refresh} />
            </div>
          )}

          {pendingDraft && (
            <Link
              to={`/studio/preview/${pendingDraft.id}?mode=editor`}
              data-testid="bienvenue-resume-draft"
              className="mt-6 group rounded-xl border border-[rgba(127,179,255,0.32)] bg-gradient-to-br from-[rgba(45,123,255,0.08)] to-transparent p-5 flex items-center gap-4 hover:border-[var(--mc-blue-400)] transition"
            >
              <div className="grid place-items-center h-11 w-11 rounded-lg bg-[rgba(45,123,255,0.18)] border border-[rgba(127,179,255,0.36)] text-[#0284C7] shrink-0">
                <PlayCircle size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-[#0284C7] font-semibold">
                  <span className="px-1.5 py-0.5 rounded bg-[var(--mc-blue-400)] text-[#0F2B5B]">En cours</span>
                  Simulation Studio
                </div>
                <div className="text-[#0F2B5B] text-[14.5px] font-medium leading-tight mt-1 truncate">
                  Vous avez 1 simulation en cours
                  {pendingDraft.brand_name && (
                    <span className="text-[var(--mc-muted)]"> — {pendingDraft.brand_name}</span>
                  )}
                </div>
                <p className="text-[12px] text-[var(--mc-muted)] mt-0.5">
                  Dernière modification le{" "}
                  {new Date(pendingDraft.updated_at || pendingDraft.created_at).toLocaleDateString("fr-FR", {
                    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                  })}
                  . Reprenez là où vous vous êtes arrêté pour finaliser votre cahier des charges.
                </p>
              </div>
              <ArrowRight
                size={18}
                className="text-[#0284C7] group-hover:text-[#0F2B5B] group-hover:translate-x-0.5 transition shrink-0"
              />
            </Link>
          )}
        </div>
      </section>

      {/* TWO ACTION CARDS */}
      <section className="py-6 lg:py-10">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="grid md:grid-cols-2 gap-5">
            {/* Card 1 — Studio (highlighted) */}
            <Link
              to="/studio"
              data-testid="bienvenue-card-studio"
              className="group mc-card relative p-7 border-2 border-[rgba(110,231,197,0.45)] hover:border-[var(--mc-mint)] bg-gradient-to-br from-[rgba(110,231,197,0.06)] to-transparent transition flex flex-col"
            >
              <div className="absolute top-4 right-4">
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded-full bg-[var(--mc-mint)] text-[#0F2B5B]"
                  data-testid="bienvenue-card-studio-badge"
                >
                  <Sparkles size={10} /> Nouveau
                </span>
              </div>

              <div className="grid place-items-center h-14 w-14 rounded-xl bg-[rgba(110,231,197,0.14)] border border-[rgba(110,231,197,0.4)] text-[var(--mc-mint)] mb-5">
                <LayoutDashboard size={26} />
              </div>

              <h2 className="font-display text-[#0F2B5B] text-2xl font-semibold leading-tight">
                Visualiser ma plateforme
              </h2>
              <p className="mt-3 text-[13.5px] text-[var(--mc-muted)] leading-relaxed flex-1">
                Configurez et simulez votre plateforme de télémédecine en{" "}
                <strong className="text-[#0F2B5B]">5 minutes</strong>. Devis personnalisé inclus.
              </p>

              <div className="mt-6 inline-flex items-center gap-2 text-[var(--mc-mint)] text-[13px] font-medium group-hover:gap-3 transition-all">
                Lancer le configurateur
                <ArrowRight size={15} />
              </div>
            </Link>

            {/* Card 2 — Standard */}
            <Link
              to="/mssante/creer-boite"
              data-testid="bienvenue-card-mssante"
              className="group mc-card p-7 hover:border-[var(--mc-blue-400)] transition flex flex-col"
            >
              <div className="grid place-items-center h-14 w-14 rounded-xl bg-[rgba(45,123,255,0.14)] border border-[rgba(127,179,255,0.36)] text-[#0284C7] mb-5">
                <Mail size={26} />
              </div>

              <h2 className="font-display text-[#0F2B5B] text-2xl font-semibold leading-tight">
                Créer ma boîte MSSanté
              </h2>
              <p className="mt-3 text-[13.5px] text-[var(--mc-muted)] leading-relaxed flex-1">
                Activez votre messagerie sécurisée de santé pour échanger avec vos confrères.
              </p>

              <div className="mt-6 inline-flex items-center gap-2 text-[#0284C7] text-[13px] font-medium group-hover:gap-3 transition-all">
                Créer ma boîte MSS
                <ArrowRight size={15} />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* STATUS RECAP */}
      <section className="py-10 lg:py-14">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#0284C7] mb-5">
            Récapitulatif de votre espace
          </div>

          <div className="mc-card divide-y divide-[rgba(127,179,255,0.12)] overflow-hidden">
            {statuses.map((s) => (
              <StatusRow
                key={s.key}
                Icon={s.icon}
                label={s.label}
                sublabel={s.sublabel}
                tone={s.tone}
                text={loading && s.key !== "psc" ? "…" : s.text}
                testid={`bienvenue-status-${s.key}`}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function StatusRow({ Icon, label, sublabel, tone, text, testid }) {
  const toneClasses = {
    ok: "border-[rgba(110,231,197,0.4)] text-[var(--mc-mint)] bg-[rgba(110,231,197,0.06)]",
    warn: "border-[rgba(255,170,80,0.4)] text-[#B45309] bg-[rgba(255,170,80,0.06)]",
    muted: "border-[rgba(127,179,255,0.3)] text-[var(--mc-muted)] bg-[rgba(45,123,255,0.04)]",
    soon: "border-[rgba(127,179,255,0.32)] text-[#0284C7] bg-[rgba(45,123,255,0.08)]",
  }[tone] || "border-[rgba(127,179,255,0.3)] text-[var(--mc-muted)] bg-[rgba(45,123,255,0.04)]";

  const ToneIcon = tone === "ok" ? Check : tone === "soon" ? Clock : null;

  return (
    <div className="flex items-center gap-4 p-5" data-testid={testid}>
      <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(127,179,255,0.08)] border border-[rgba(127,179,255,0.18)] text-[#0284C7] shrink-0">
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[#0F2B5B] text-[14px] font-medium leading-tight">{label}</div>
        {sublabel && (
          <div className="text-[12px] text-[var(--mc-muted)] mt-0.5 truncate">{sublabel}</div>
        )}
      </div>
      <span
        className={`text-[11px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full border inline-flex items-center gap-1.5 ${toneClasses}`}
      >
        {ToneIcon && <ToneIcon size={11} />}
        {text}
      </span>
    </div>
  );
}
